import { flexRender, getCoreRowModel, getFilteredRowModel, Row, RowSelectionState, useReactTable } from "@tanstack/react-table";
import { useCallback, useEffect, useState, ReactNode } from "react";
import "./styles.css";
import { Box, Button, Flex, Input, InputGroup, InputLeftElement, Icon, Text } from "@chakra-ui/react";
import { Search2Icon, SearchIcon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { SortIcon } from "../SortIcon/SortIcon";
import { RootState, useAppDispatch, useAppSelector } from "../../store";
import { fetchAllDepartments } from "../../store/slices/departments";
import { debounce } from "lodash";
import { SortingTypes } from "../../helpers/SortingTypes";
import { FiPlus } from "react-icons/fi";

const colourStyles = {
  control: (styles: any) => ({
    ...styles,
    width: "300px",
    maxWidth: "473px",
    borderColor: "#D6D6D6",
    zIndex: 1,
  }),
  menu: (styles: any) => ({
    ...styles,
    zIndex: 5000,
  }),
};

interface TableProps {
  list: any[];
  columns: any[];
  addClick?: () => void;
  clickHandler?: (id: string, event?: React.MouseEvent) => void;
  withoutSearch?: boolean;
  withoutNavigate?: boolean;
  maxHeight?: string;
  minHeight?: string;
  canSelect?: boolean;
  rowClick?: (row: Row<any>) => void;
  departmentFilter?: boolean;
  handleSearch?: (value: string) => void;
  setDepartmentId?: (value: string | undefined) => void;
  sortHandler?: (column: any) => void;
  isShowAddButton?: boolean;
  sortState?: {
    column: string;
    direction: SortingTypes | null | undefined;
  };
  leftExtra?: ReactNode;
  belowControls?: ReactNode;
  placeHolderInput?: string;
  noFilter?: boolean;
  searchValue?: string;
  getRowStyle?: (row: any) => React.CSSProperties;
}

export const Table = ({
  list,
  columns,
  addClick,
  clickHandler,
  withoutSearch = false,
  withoutNavigate = false,
  maxHeight = "68vh",
  minHeight = "500px",
  canSelect = false,
  departmentFilter = false,
  rowClick,
  handleSearch,
  setDepartmentId,
  sortHandler,
  sortState,
  leftExtra,
  belowControls,
  isShowAddButton = true,
  placeHolderInput = "",
  noFilter = false,
  searchValue,
  getRowStyle,
}: TableProps) => {
  const [filteredList, setFilteredList] = useState<any[]>(list);
  const [columnFilters, setColumnFilters] = useState<any>([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const [searchValueState, setSearchValueState] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    setFilteredList(list);
  }, [list]);

  // Синхронизируем внешнее значение поиска с внутренним состоянием
  useEffect(() => {
    if (searchValue !== undefined) {
      setSearchValueState(searchValue);
    }
  }, [searchValue]);

  const [columnsLength, setColumnsLength] = useState(0);
  const dispatch = useAppDispatch();

  const table = useReactTable({
    data: filteredList,
    columns,
    enableRowSelection: true,
    enableMultiRowSelection: false,
    defaultColumn: {
      enableColumnFilter: false,
      enableSorting: false,
      size: 160,
      minSize: 40,
      maxSize: 600,
      enableResizing: false,
    },
    state: {
      columnFilters,
      globalFilter,
      rowSelection,
    },
    onRowSelectionChange: setRowSelection,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: (row: any, columnId, filterValue) => {
      if (!filterValue) {
        return true;
      }
      const value = row.getValue(columnId);
      if (!value) return false;

      const rowValues = Object.values(value).join("").toLowerCase();
      return rowValues?.includes(filterValue.toString().toLowerCase());
    },
    onGlobalFilterChange: setGlobalFilter,
  });

  useEffect(() => {
    table.getHeaderGroups().map((el) => setColumnsLength((prevLength) => prevLength + el.headers.length));
  }, [table]);

  const { departments } = useAppSelector((state: RootState) => state.departments);

  const [departmentList, setDepartmentList] = useState<{ value: string; label: string }[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState<{
    value: string;
    label: string;
  } | null>(null);

  useEffect(() => {
    if (departmentFilter) {
      const resultDepartments = departments.items.map((el) => ({
        value: String(el.id),
        label: el.name,
      }));
      setDepartmentList(resultDepartments);
    }
  }, [departments, departmentFilter]);

  useEffect(() => {
    if (departmentFilter) {
      dispatch(fetchAllDepartments());
    }
  }, [departmentFilter, dispatch]);

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      handleSearch?.(value);
    }, 500),
    [handleSearch],
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValueState(e.target.value);
    debouncedSearch(e.target.value);
  };

  useEffect(() => {
    if (selectedDepartment !== null) {
      setDepartmentId?.(selectedDepartment.value);
    } else {
      setDepartmentId?.(undefined);
    }
  }, [selectedDepartment]);

  return (
    <Flex w="100%" flexDirection="column" minH={minHeight}>
      <Flex
        justifyContent="space-between"
        marginBottom="16px"
        backgroundColor="white"
        padding="24px"
        borderRadius="8px"
        border="1px solid #e1e7ef"
        display={noFilter ? "none" : "flex"}
      >
        {!withoutSearch && (
          <Flex gap="24px">
            <InputGroup w="309px">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.300" />
              </InputLeftElement>
              <Input type="text" w="309px" value={searchValueState} onChange={handleSearchChange} placeholder={placeHolderInput} fontSize="14px" />
            </InputGroup>
            {departmentFilter && (
              <Select
                menuPosition="absolute"
                className="basic-single"
                classNamePrefix="select"
                isSearchable={false}
                isClearable
                name="color"
                styles={colourStyles}
                options={departmentList}
                value={selectedDepartment}
                placeholder="Выберите подразделение"
                onChange={(value) => {
                  setSelectedDepartment(value);
                }}
              />
            )}
            {leftExtra}
          </Flex>
        )}
        {!withoutNavigate && isShowAddButton && (
          <Button
            leftIcon={<Icon as={FiPlus as any} boxSize={4} />}
            onClick={addClick}
            bg="#2563eb"
            color="white"
            _hover={{ bg: "#2563eb" }}
            _active={{ bg: "#2563eb" }}
          >
            Создать
          </Button>
        )}
      </Flex>

      {belowControls}

      <Box
        className="scroll-table-body-base"
        backgroundColor="white"
        maxH={maxHeight}
        border="1px solid #e1e7ef"
        borderRadius="8px"
        boxShadow="sm"
        overflow="auto"
        marginRight="0px"
      >
        <table
          style={{
            minWidth: "max-content",
            width: "100%",
            borderCollapse: "separate",
            borderSpacing: "0 0",
            height: "100%",
            tableLayout: "auto",
          }}
        >
          <thead
            style={{
              position: "sticky",
              top: 0,
              left: 0,
              backgroundColor: "white",
              textAlign: "left",
              zIndex: 100,
            }}
          >
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    style={{
                      padding: "16px",
                      cursor: header.column.getCanSort() ? "pointer" : "default",
                      backgroundColor: "white",
                      position: "sticky",
                      top: 0,
                      zIndex: 100,
                      width: `${header.column.getSize()}px`,
                      minWidth: `${header.column.columnDef.minSize ?? 40}px`,
                    }}
                  >
                    <Flex alignItems="center" gap="8px">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getCanSort() && (
                        <Box
                          onClick={(e) => {
                            e.stopPropagation();
                            if (sortHandler) {
                              const nextDirection =
                                sortState?.direction === SortingTypes.ASC
                                  ? SortingTypes.DESC
                                  : sortState?.direction === SortingTypes.DESC
                                    ? null
                                    : SortingTypes.ASC;
                              sortHandler({
                                id: header.column.id,
                                direction: nextDirection,
                              });
                            }
                          }}
                        >
                          <SortIcon sort={sortState?.direction} />
                        </Box>
                      )}
                    </Flex>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody
            style={{
              borderCollapse: "separate",
              borderSpacing: "0 10px",
            }}
          >
            {table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  style={{
                    height: "55px",
                    backgroundColor: row.getIsSelected() ? "#42AAFF" : "white",
                    color: "#3b515e",
                    borderBottom: "1px solid #e1e7ef",
                    ...(getRowStyle ? getRowStyle(row.original) : {}),
                  }}
                  onMouseDown={(e) => {
                    // Проверяем, есть ли выделенный текст
                    const selection = window.getSelection();
                    if (selection && selection.toString().length > 0) {
                      return; // Если есть выделение, не выполняем клик
                    }

                    if (canSelect && rowClick) {
                      rowClick(row);
                    } else {
                      if (clickHandler) {
                        if (e.ctrlKey || e.metaKey || e.button === 1) {
                          window.open(`${window.location.pathname}/${row.original.id}`, "_blank");
                        } else {
                          clickHandler(row.original.id, e);
                        }
                      } else if (!withoutNavigate) {
                        if (e.ctrlKey || e.metaKey || e.button === 1) {
                          window.open(`${window.location.pathname}/${row.original.id}`, "_blank");
                        } else if (e.button === 0) {
                          // Только левая кнопка мыши для навигации
                          navigate(`${row.original.id}`);
                        }
                      }
                    }
                  }}
                >
                  {row.getVisibleCells().map((cell, cellIndex) => {
                    const rowStyle = getRowStyle ? getRowStyle(row.original) : {};
                    const hasLeftBorder = cellIndex === 0 && rowStyle.borderLeft;
                    
                    return (
                      <td
                        key={cell.id}
                        style={{
                          textAlign: "left",
                          borderBottom: "1px solid #e1e7ef",
                          padding: hasLeftBorder ? "16px 16px 16px 12px" : "16px",
                          width: `${cell.column.getSize()}px`,
                          minWidth: `${cell.column.columnDef.minSize ?? 40}px`,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          position: "relative",
                        }}
                      >
                        {hasLeftBorder && (
                          <Box
                            position="absolute"
                            left="0"
                            top="0"
                            bottom="0"
                            width="4px"
                            backgroundColor="#FF9800"
                          />
                        )}
                        {clickHandler || withoutNavigate ? (
                          <>{flexRender(cell.column.columnDef.cell, cell.getContext())}</>
                        ) : (
                          <a
                            href=""
                            onClick={(e) => {
                              e.preventDefault();
                            }}
                            style={{
                              textDecoration: "none",
                              color: "inherit",
                              display: "block",
                            }}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </a>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columnsLength}>
                  <Flex alignItems="center" justifyContent="center" flexDirection="column" padding="64px 0">
                    <Search2Icon w="64px" h="64px" />
                    <Text fontSize={32}>Ничего не найдено</Text>
                  </Flex>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Box>
    </Flex>
  );
};
